import { useCallback, useState } from 'react';
import { SlackWizardConfig } from '../SlackWizard';
import { slackApi } from '../api/slackApi';

export interface SlackPreviewResult {
	channels: Array<{
		channel: string;
		channelName?: string;
		renderedContent: {
			text?: string;
			blocks?: any[];
			format: string;
		};
		valid: boolean;
		errors?: string[];
		warnings?: string[];
	}>;
	summary: {
		validChannels: number;
		invalidChannels: number;
		tokensResolved: number;
		tokensFound: string[];
	};
	globalErrors?: string[];
	globalWarnings?: string[];
}

export function useSlackPreview() {
	const [preview, setPreview] = useState<SlackPreviewResult | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const generatePreview = useCallback(
		async (config: SlackWizardConfig, sampleContext: Record<string, any>) => {
			if (!config.credentialId || config.channels.length === 0) {
				setError('Missing required configuration for preview');
				return;
			}

			setIsLoading(true);
			setError(null);

			try {
				const previewData = await slackApi.preview({
					credentialId: config.credentialId,
					channels: config.channels,
					format: config.format,
					content: config.content,
					threading: config.threading,
					options: config.options,
					outputs: config.outputs,
					sampleContext: {
						data: sampleContext,
					},
					preview: true,
				});

				setPreview(previewData);
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : 'Failed to generate preview';
				setError(errorMessage);
				setPreview(null);
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	const clearPreview = useCallback(() => {
		setPreview(null);
		setError(null);
	}, []);

	return {
		preview,
		isLoading,
		error,
		generatePreview,
		clearPreview,
	};
}


